// @flow
import { createSelector } from "reselect";
import { selectIsMultipleEnabled, getWidgetConfig } from "root/selectors";
import {
	selectTabIsIdSelected,
	selectTabReachedMaxSelectionWithContext,
	getListMultipleIdsInRange,
	selectTabAvailableUntilMax,
} from "../../common/selectors";
import consts from "../data/consts";

import type {
	BreadCrumbItem, GoogleDriveRecord, GoogleDriveState, TabData,
} from "../data/types";

const TAB = "gdrive";

const selectReachedMaxSelection = selectTabReachedMaxSelectionWithContext(TAB);
const selectIsIdSelected = selectTabIsIdSelected(TAB);
const selectAvailableUntilMax = selectTabAvailableUntilMax(TAB);

const selectGoogleDrive = (state: GoogleDriveState): GoogleDriveRecord => state.gdrive;

const selectCurrentContext: GoogleDriveState => string = createSelector(
	selectGoogleDrive,
	(gdrive) => gdrive.currentContext,
);

const selectSearchTerm: GoogleDriveState => ?string = createSelector(
	selectGoogleDrive,
	(gdrive) => gdrive.lists[consts.CONTEXTS.SEARCH].params.term,
);

const selectUser: GoogleDriveState => ?{ name: string } = createSelector(
	selectGoogleDrive,
	(gdrive) => gdrive.user,
);

const selectTabData: GoogleDriveState => TabData = createSelector(
	selectIsMultipleEnabled,
	selectUser,
	selectCurrentContext,
	(isMultiple, user, currentContext): TabData => ({
		isMultiple,
		user,
		isLoggedIn: !!user,
		currentContext,
	}),
);

const selectConfigClientId: GoogleDriveState => ?string = createSelector(
	getWidgetConfig,
	(config) => config.googleDriveClientId,
);

const selectPanelOpen: GoogleDriveState => boolean = createSelector(
	selectGoogleDrive,
	(gdrive) => gdrive.panelIsOpen,
);

const selectSort: GoogleDriveState => string = createSelector(
	selectGoogleDrive,
	(gdrive) => gdrive.sort,
);

const selectOrderBy: GoogleDriveState => string = createSelector(
	selectGoogleDrive,
	(gdrive) => gdrive.orderBy,
);

const selectIsSearch: GoogleDriveState => boolean = createSelector(
	selectCurrentContext,
	(context) => context === consts.CONTEXTS.SEARCH,
);

const selectListFiles: GoogleDriveState => Object = createSelector(
	selectGoogleDrive,
	selectCurrentContext,
	(gdrive, context) => gdrive.lists[context].files,
);

const getLists: GoogleDriveState => Object = createSelector(
	selectGoogleDrive,
	(gdrive) => gdrive.lists,
);

const selectMultipleIdsInRange: GoogleDriveState => (string, string) => string[] = createSelector(
	getLists,
	selectListFiles,
	(lists, listFiles) => (id: string, context: string) => getListMultipleIdsInRange({
		...lists[context],
		items: listFiles.data,
	}, id),
);

const selectSelectedIds: GoogleDriveState => string[] = createSelector(
	getLists,
	selectCurrentContext,
	(lists, context) => lists[context].selected,
);

const selectBreadcrumbsPath: GoogleDriveState => BreadCrumbItem[] = createSelector(
	selectGoogleDrive,
	(gdrive) => gdrive.breadcrumbPath,
);

const selectIsFetchingUploadData: GoogleDriveState => boolean = createSelector(
	selectGoogleDrive,
	(grdive: GoogleDriveRecord) => grdive.fetchingUploadData,
);

const selectIsWaitingForSignIn: GoogleDriveState => boolean = createSelector(
	selectGoogleDrive,
	(grdive: GoogleDriveRecord) => !grdive.userNotLoggedIn && grdive.waitForSignIn,
);

export {
	selectGoogleDrive,
	selectBreadcrumbsPath,
	selectTabData,
	selectIsIdSelected,
	selectReachedMaxSelection,
	selectCurrentContext,
	selectMultipleIdsInRange,
	selectAvailableUntilMax,
	selectConfigClientId,
	selectPanelOpen,
	selectSort,
	selectOrderBy,
	selectIsSearch,
	selectListFiles,
	selectSelectedIds,
	selectSearchTerm,
	selectIsFetchingUploadData,
	selectUser,
	selectIsWaitingForSignIn,
};
